import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Megaphone, RefreshCw, Link2, Unlink, Loader2, Search, TrendingUp, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmtNum = (n) => Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
const fmtMoney = (n) => Intl.NumberFormat("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

function Stat({ label, value, money }) {
  return (
    <div>
      <p className="num-display text-lg font-extrabold leading-none">{money ? fmtMoney(value) : fmtNum(value)}</p>
      <p className="text-[11px] text-neutral-500">{label}</p>
    </div>
  );
}

function CampaignRow({ c, onUnlink, unlinking }) {
  return (
    <div data-testid="meta-campaign-row" className="rounded-xl border border-black/[0.06] bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold leading-tight">{c.name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] capitalize">{(c.objective || "").replaceAll("_", " ").toLowerCase() || "campaign"}</Badge>
            <Badge className={`text-[10px] ${c.effective_status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>{c.effective_status}</Badge>
          </div>
        </div>
        {onUnlink && (
          <Button data-testid={`meta-unlink-${c.meta_id}`} onClick={() => onUnlink(c)} disabled={unlinking} variant="ghost" size="sm" className="shrink-0 text-neutral-400 hover:text-red-600">
            {unlinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 border-t border-black/[0.06] pt-3">
        <Stat label="Spend · 7d" value={c.d7.spend} money />
        <Stat label="Impr · 7d" value={c.d7.impressions} />
        <Stat label="Clicks · 7d" value={c.d7.clicks} />
        <Stat label="Spend · 30d" value={c.d30.spend} money />
        <Stat label="Impr · 30d" value={c.d30.impressions} />
        <Stat label="Clicks · 30d" value={c.d30.clicks} />
      </div>
    </div>
  );
}

function LinkDialog({ artistId, songs, onLinked }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [q, setQ] = useState("");
  const [song, setSong] = useState("__none__");
  const [linking, setLinking] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/meta/campaigns")
      .then((r) => setCampaigns(r.data.campaigns || []))
      .catch(() => toast.error("Couldn't load Meta campaigns"))
      .finally(() => setLoading(false));
  }, [open]);

  const link = async (c) => {
    setLinking(c.meta_id);
    try {
      await api.post("/meta/link", { meta_id: c.meta_id, artist_id: artistId, song_id: song === "__none__" ? null : song });
      toast.success(`Linked “${c.name}”`);
      onLinked();
      setOpen(false);
    } catch {
      toast.error("Couldn't link campaign");
    } finally {
      setLinking("");
    }
  };

  const filtered = campaigns.filter((c) => c.name?.toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="meta-link-campaign-btn" variant="outline" className="rounded-full border-[#35606E]/30 bg-white text-[#35606E] hover:bg-[#35606E]/5">
          <Link2 className="mr-1.5 h-4 w-4" /> Link campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Link a Meta campaign</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input data-testid="meta-link-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search campaigns…" className="pl-9" />
          </div>
          <Select value={song} onValueChange={setSong}>
            <SelectTrigger data-testid="meta-link-song-select" className="sm:w-56"><SelectValue placeholder="Assign to song" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No specific song</SelectItem>
              {songs.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-neutral-500">No active campaigns found.</p>
          ) : filtered.map((c) => (
            <div key={c.meta_id} className="flex items-center justify-between gap-3 rounded-lg border border-black/[0.06] p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{c.name}</p>
                <p className="text-xs text-neutral-500">30d spend {fmtMoney(c.d30.spend)} · {fmtNum(c.d30.clicks)} clicks{c.linked_artist_id ? " · already linked" : ""}</p>
              </div>
              <Button data-testid={`meta-link-do-${c.meta_id}`} onClick={() => link(c)} disabled={linking === c.meta_id} size="sm" className="shrink-0 rounded-full bg-[#35606E] hover:bg-[#294b57]">
                {linking === c.meta_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link"}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter className="text-xs text-neutral-400">Spend/impressions/clicks are pulled live from Meta — real numbers only.</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MetaAdsSection({ artistId, songs = [], className = "" }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [unlinking, setUnlinking] = useState(false);

  const load = useCallback(() => {
    api.get(`/artists/${artistId}/meta`).then((r) => setData(r.data)).catch(() => setData({ status: "unavailable", songs: [], unassigned: [], suggestions: [], totals: null }));
  }, [artistId]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/meta/refresh"); load(); toast.success("Refreshed from Meta"); }
    catch { toast.error("Couldn't refresh"); }
    finally { setRefreshing(false); }
  };

  const linkSuggestion = async (c) => {
    try {
      await api.post("/meta/link", { meta_id: c.meta_id, artist_id: artistId, song_id: c.suggested_song_id || null });
      toast.success(`Linked “${c.name}”`);
      load();
    } catch { toast.error("Couldn't link campaign"); }
  };

  const unlink = async (c) => {
    setUnlinking(true);
    try { await api.delete(`/meta/link/${c.meta_id}`); load(); }
    catch { toast.error("Couldn't unlink"); }
    finally { setUnlinking(false); }
  };

  if (!data) return null;

  const hasLinked = (data.songs?.length || 0) + (data.unassigned?.length || 0) > 0;
  const t = data.totals;

  return (
    <section className={className} data-testid="meta-ads-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-[#35606E]" />
          <h2 className="font-display text-xl font-bold">Meta Ads</h2>
        </div>
        <div className="flex items-center gap-2">
          {data.status === "ok" && <LinkDialog artistId={artistId} songs={songs} onLinked={load} />}
          <Button data-testid="meta-refresh-btn" onClick={refresh} disabled={refreshing} variant="ghost" size="sm" className="text-neutral-500">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {data.status !== "ok" ? (
        <div data-testid="meta-unavailable" className="pb-card flex flex-col items-center gap-2 p-8 text-center">
          <Megaphone className="h-7 w-7 text-neutral-300" />
          <p className="text-sm font-medium text-neutral-600">{data.status === "no_token" ? "Meta ad account not connected" : "Meta is unavailable right now"}</p>
          <p className="text-xs text-neutral-500">{data.message || "Real numbers only — nothing shown until Meta responds."}</p>
        </div>
      ) : (
        <>
          {hasLinked && t && (
            <div data-testid="meta-totals" className="pb-card mb-5 grid grid-cols-3 gap-4 p-5 sm:grid-cols-6">
              <Stat label="Spend · 7d" value={t.d7.spend} money />
              <Stat label="Impr · 7d" value={t.d7.impressions} />
              <Stat label="Clicks · 7d" value={t.d7.clicks} />
              <Stat label="Spend · 30d" value={t.d30.spend} money />
              <Stat label="Impr · 30d" value={t.d30.impressions} />
              <Stat label="Clicks · 30d" value={t.d30.clicks} />
            </div>
          )}

          {data.songs?.map((s) => (
            <div key={s.song_id} className="mb-6">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-neutral-400">
                <TrendingUp className="h-4 w-4" /> {s.title}
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {s.campaigns.map((c) => <CampaignRow key={c.meta_id} c={c} onUnlink={unlink} unlinking={unlinking} />)}
              </div>
            </div>
          ))}

          {data.unassigned?.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-400">Not assigned to a song</h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {data.unassigned.map((c) => <CampaignRow key={c.meta_id} c={c} onUnlink={unlink} unlinking={unlinking} />)}
              </div>
            </div>
          )}

          {data.suggestions?.length > 0 && (
            <div className="mb-6">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#35606E]">
                <Sparkles className="h-4 w-4" /> Suggested matches
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {data.suggestions.map((c) => (
                  <div key={c.meta_id} data-testid="meta-suggestion" className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-[#35606E]/30 bg-[#EEF6F4]/40 p-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium leading-tight">{c.name}</p>
                      <p className="text-xs text-neutral-500">{c.matched_on} · 30d spend {fmtMoney(c.d30.spend)}</p>
                    </div>
                    <Button data-testid={`meta-link-suggest-${c.meta_id}`} onClick={() => linkSuggestion(c)} size="sm" className="shrink-0 rounded-full bg-[#35606E] hover:bg-[#294b57]">
                      <Link2 className="mr-1.5 h-3.5 w-3.5" /> Link
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!hasLinked && data.suggestions?.length === 0 && (
            <div data-testid="meta-empty" className="pb-card flex flex-col items-center gap-2 p-10 text-center">
              <Megaphone className="h-8 w-8 text-neutral-300" />
              <p className="text-sm font-medium text-neutral-600">No ads linked for this artist yet</p>
              <p className="max-w-md text-xs text-neutral-500">Use “Link campaign” to connect a Meta campaign and see 7-day and 30-day spend, impressions and clicks — grouped by song.</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}
